// src/encounter/risk/services/use-risk-assessments.ts
// The Risk tab's persistence hook — EncounterMetaData Fall/Pain Assessment (data-layer handoff,
// 2026-09-13). Replaces the earlier QuestionnaireResponse-backed `risk-persistence.ts` (removed):
// Risk no longer writes any `risk-*` group into the shared QuestionnaireResponse at all.
//
// Contract mirrored here (see the handoff for the source of each rule):
//   1. LOAD = two independent GETs when the encounter opens. Merge-not-replace: an empty/absent
//      response leaves the local blank placeholder in place instead of clearing to `[]`.
//   2. Editing (including "Done") is LOCAL ONLY and marks one shared dirty flag — it never hits
//      the network by itself.
//   3. SAVE IS BATCHED: fall + pain POST together, once, whenever the container is dirty, fired
//      on "encounter-level save" — approximated as the page calling `flush` on a step change,
//      the owner (the encounter page) unmounting, or the tab being hidden/closed. It stays
//      independent of the page's QuestionnaireResponse autosave: Risk answers never ride the QR
//      (PROD-2577 — the renderer's Risk answers reach this hook through `use-risk-bridge.ts`).
//   4. SAVE always ships the WHOLE current array, never a delta.
//   5. A new save cancels an in-flight save of the SAME resource (AbortController; last write
//      wins) — fall and pain are aborted independently since they are two separate calls.
//   6. The silent-save-failure PARITY-DEFECT is reproduced deliberately — see the swallow below.
//      Do not "fix" it without the follow-up ticket. EXCEPT a signed-note refusal (PROD-2810 /
//      rule 38): a 409 `encounter-signed` is reported through `saveRefused`, and nothing is sent again. (The fall-vs-pain serialization asymmetry
//      this used to list was a misreading of Flutter and is fixed: both are single-encoded,
//      PROD-2354.)
//   7. PROD-3142 (Scrips.Patient #1051): when the API sends a list's `version`, every save of that list sends the
//      version it was edited from (`expectedVersion`) and keeps the version the save returns. Such saves are QUEUED per
//      list, never aborted: an aborted request may still land, and the next save would then look stale against our own
//      write. A 412 `stale-version` (another window or a colleague changed the list) is latched per list in
//      `saveConflict`: that list is not sent again, its edits stay here, and the page says to reload. An API without
//      versions keeps 5. exactly as before.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { postFallAssessments, postPainAssessments } from '@/shared/api/risk-assessment-service';
import { fallQueryOptions, painQueryOptions } from './risk-assessment-queries';
import { isSignedNoteRefusal, isStaleRiskSave } from '../../lib/signed-refusal';
import { NoteSessionContext, isNoteSessionDiscarded } from '../../services/note-session';
import {
  emptyFallAssessment,
  emptyPainAssessment,
  type FallAssessmentEntry,
  type PainAssessmentEntry,
} from '../lib/risk-assessment-types';

// The keys and the reads live in risk-assessment-queries.ts, shared with the pre-visit nurse card's read-only hook.
export { riskAssessmentKeys } from './risk-assessment-queries';

export interface UseRiskAssessments {
  fallAssessments: FallAssessmentEntry[];
  painAssessments: PainAssessmentEntry[];
  /** Replace the whole Fall list (the card always rewrites its own full list) — LOCAL, marks
   *  the container dirty. Never itself triggers a network call. */
  updateFallAssessments: (list: FallAssessmentEntry[]) => void;
  updatePainAssessments: (list: PainAssessmentEntry[]) => void;
  /** True once the initial GETs have both settled — success, 404-as-empty, OR a failed read. A
   *  failed read must not hold the Risk step back forever: a Pain GET 500 used to leave this false
   *  for good, so the Risk renderer never mounted and the step drew its chip rail over nothing. */
  isLoaded: boolean;
  /** Which initial GET failed (after React Query's retries). A failed list is never POSTed —
   *  the server may hold a record this client could not read, and a save would overwrite it. */
  readFailed: { fall: boolean; pain: boolean };
  /** What the server returned, verbatim — `undefined` until settled, `[]` when nothing is stored.
   *  The Risk bridge hydrates the renderer from these heads rather than from the lists above,
   *  which adopt the server rows one effect later and hold a blank placeholder until then. */
  serverFallAssessments: FallAssessmentEntry[] | undefined;
  serverPainAssessments: PainAssessmentEntry[] | undefined;
  /** Save now, if anything changed since the last save — the encounter-level save trigger (the
   *  page calls it on a step change, as the Risk panel's unmount used to). No-op when clean. Returns whether a save
   *  was started (PROD-2947: the frame v2 sign waits for it). `keepalive`: the page is going away (see the hide flush). */
  flush: (opts?: { keepalive?: boolean }) => boolean;
  /** PROD-2947 · a Fall / Pain save is in flight (the frame v2 sign and the save-state header wait on it). */
  saving: boolean;
  /** PROD-2947 · frame v2: the last Fall / Pain save failed (not refused as signed); its edits are still pending. */
  saveFailed: boolean;
  /**
   * PROD-2810 / rule 38: the server refused a Fall / Pain save because the encounter is signed (409
   * `encounter-signed`). Latched for this encounter: nothing is sent again, so the refused content
   * is never re-tried, and the page tells the clinician. Every other failure stays swallowed (the
   * parity defect above).
   */
  saveRefused: boolean;
  /** PROD-3142: which list's versioned save was refused as stale (412): someone changed it since it was read. That
   *  list is not sent again in this encounter; the page tells the clinician to reload. */
  saveConflict: { fall: boolean; pain: boolean };
}

export interface UseRiskAssessmentsOptions {
  /** PROD-2947 · frame v2 (Andrew's walk, 7 Oct, B5): save on a pause, this many ms after the last edit — the note has
   *  no "Done" and no step change in one scroll, and a reload or a sign must not lose the answers. The Risk bridge sets
   *  it with the switch off too since 10 Oct. Absent → saves only on the encounter-level triggers. */
  autosaveMs?: number;
  /** PROD-2947 · frame v2 (Codex P1 on the walk fixes): a failed save (not a refusal, not an abort) is REPORTED
   *  (`saveFailed`) and its edits stay pending, so a sign never goes on over answers the server never took. Absent →
   *  the swallow below, as before (switch OFF). */
  reportFailures?: boolean;
  /** PROD-3065b · frame v2: with `reportFailures`, a failed save is retried ONCE, automatically, this many ms later —
   *  with no further edit. Until that retry settles the save counts as in flight (`saving`: the header says saving, a
   *  sign waits). If the retry fails too, the edits stay pending and the failure is reported (`saveFailed`). A new
   *  edit is a new save with its own one retry. Absent → no automatic retry. */
  retryMs?: number;
}

export function useRiskAssessments(encounterId: string | null, options: UseRiskAssessmentsOptions = {}): UseRiskAssessments {
  const { autosaveMs, reportFailures = false, retryMs } = options;
  // The visit's note session (PROD-3107): once the leave guard discards it, no pending Risk edit is sent.
  const noteSession = useContext(NoteSessionContext);
  // Each read keeps the list's version beside it (PROD-3142), so the version is the one of the list this client adopted.
  const fallQuery = useQuery(fallQueryOptions(encounterId));
  const painQuery = useQuery(painQueryOptions(encounterId));

  const [fallAssessments, setFallAssessments] = useState<FallAssessmentEntry[]>(() => [emptyFallAssessment()]);
  const [painAssessments, setPainAssessments] = useState<PainAssessmentEntry[]>(() => [emptyPainAssessment()]);

  // One-time hydration per encounter — merge-not-replace (spec §4): an EMPTY server list leaves
  // the local placeholder untouched; a non-empty one adopts the server rows exactly once, so a
  // later background refetch can't clobber in-progress local edits. Computed IN RENDER, not an
  // effect: `fallQuery.data`/`painQuery.data` are already resolved by the time this hook body
  // runs, so there is nothing an effect would wait for that this render doesn't already have —
  // it is React's "adjust state when an input changes" shape, guarded by encounterId so it fires
  // at most once per encounter (the same guarantee the old ref-gated effect made, one render
  // later).
  const [hydratedFallFor, setHydratedFallFor] = useState<string | null>(null);
  const [hydratedPainFor, setHydratedPainFor] = useState<string | null>(null);

  if (encounterId && fallQuery.data !== undefined && hydratedFallFor !== encounterId) {
    setHydratedFallFor(encounterId);
    if (fallQuery.data.entries.length > 0) setFallAssessments(fallQuery.data.entries);
  }
  if (encounterId && painQuery.data !== undefined && hydratedPainFor !== encounterId) {
    setHydratedPainFor(encounterId);
    if (painQuery.data.entries.length > 0) setPainAssessments(painQuery.data.entries);
  }

  // PROD-3142: the version of each list as the server last answered — the one adopted at hydration, then each versioned
  // save's answer. Seeded after the hydration commit (a ref is never written in render); reset per encounter.
  const versionRef = useRef<{ for: string | null; seeded: { fall: boolean; pain: boolean }; fall?: string; pain?: string }>({
    for: null,
    seeded: { fall: false, pain: false },
  });
  const fallVersion = fallQuery.data?.version;
  const painVersion = painQuery.data?.version;
  useEffect(() => {
    if (!encounterId) return;
    if (versionRef.current.for !== encounterId) versionRef.current = { for: encounterId, seeded: { fall: false, pain: false } };
    const v = versionRef.current;
    if (hydratedFallFor === encounterId && !v.seeded.fall) {
      v.seeded.fall = true;
      v.fall = fallVersion;
    }
    if (hydratedPainFor === encounterId && !v.seeded.pain) {
      v.seeded.pain = true;
      v.pain = painVersion;
    }
  }, [encounterId, hydratedFallFor, hydratedPainFor, fallVersion, painVersion]);

  // ── Dirty tracking + batched flush ─────────────────────────────────────────────────────────
  const dirtyRef = useRef(false);
  const fallAssessmentsRef = useRef(fallAssessments);
  const painAssessmentsRef = useRef(painAssessments);
  // Refs are written AFTER commit, never during render (react-hooks/refs — a ref write in render
  // is unsafe under Strict Mode's double-render). `flush` below only ever runs from an event (a
  // step change, a visibility change, an unmount) — always after some prior commit — so reading
  // the ref there is exactly as current as the direct-in-render assignment used to be.
  useEffect(() => {
    fallAssessmentsRef.current = fallAssessments;
  }, [fallAssessments]);
  useEffect(() => {
    painAssessmentsRef.current = painAssessments;
  }, [painAssessments]);

  // PROD-3065b: each edit is a new REVISION of its list, and each revision has ONE automatic retry. A save carries the
  // revision it sent, so a failure of an older save can never use up a newer edit's retry (Codex).
  const revRef = useRef<{ fall: number; pain: number }>({ fall: 0, pain: 0 });
  const retriedRevRef = useRef<{ fall: number; pain: number }>({ fall: -1, pain: -1 });
  const updateFallAssessments = useCallback((list: FallAssessmentEntry[]) => {
    dirtyRef.current = true;
    revRef.current.fall += 1;
    setFallAssessments(list);
  }, []);
  const updatePainAssessments = useCallback((list: PainAssessmentEntry[]) => {
    dirtyRef.current = true;
    revRef.current.pain += 1;
    setPainAssessments(list);
  }, []);

  // Last-write-wins: a new flush aborts whichever call from the PREVIOUS flush is still in
  // flight, independently per resource (spec §6).
  const fallAbortRef = useRef<AbortController | null>(null);
  const painAbortRef = useRef<AbortController | null>(null);
  // PROD-3142: per encounter and per list, a versioned save in flight and whether a newer edit waits for it (sent when it
  // settles). Keyed by encounter (Codex P1): a save of one visit must never hold up, or drain into, another's.
  // `queuedKeepalive`: the waiting edit was flushed as the page went away, so its send keeps keepalive (10 Oct 2026).
  type Pair = { fall: boolean; pain: boolean };
  const queueRef = useRef<Record<string, { inFlight: Pair; queued: Pair; queuedKeepalive: Pair }>>({});
  const queueFor = (forEncounter: string) =>
    (queueRef.current[forEncounter] ??= {
      inFlight: { fall: false, pain: false },
      queued: { fall: false, pain: false },
      queuedKeepalive: { fall: false, pain: false },
    });

  // A list whose read FAILED is never written: its POST replaces the whole stored list, so saving
  // the local placeholder would overwrite a record the server holds but this client could not
  // read. Refs, so `flush` stays stable; synced after commit like the list refs above.
  const readFailed = { fall: fallQuery.isError, pain: painQuery.isError };
  const readFailedRef = useRef(readFailed);
  useEffect(() => {
    readFailedRef.current = readFailed;
  });

  // PROD-2810: a signed-note refusal, latched per encounter (state for the page, a ref for `flush`).
  const [refusedFor, setRefusedFor] = useState<string | null>(null);
  const refusedRef = useRef<string | null>(null);
  // PROD-3142: a list refused as stale, latched per encounter and per list (state for the page, a ref for `flush`). Keyed
  // by encounter (Codex P2): a late 412 from a visit left behind never clears the open visit's latch.
  type Conflict = { fall: boolean; pain: boolean };
  const [conflict, setConflict] = useState<Record<string, Conflict>>({});
  const conflictRef = useRef<Record<string, Conflict>>({});
  // Frame v2 (`reportFailures`): which resource's last save failed — its edits are kept pending and the page is told.
  const [failed, setFailed] = useState<{ fall: boolean; pain: boolean }>({ fall: false, pain: false });
  // PROD-3065b: the one automatic retry, waiting to fire (counts as a save in flight until it does).
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryWaiting, setRetryWaiting] = useState(false);
  // Scrips Agent review at 665cde84: once the owner unmounts, a failure of the save sent at unmount is ignored — no
  // retry timer may start after cleanup (nothing would ever clear it), and no state is set on an unmounted hook.
  const unmountedRef = useRef(false);
  const flushRef = useRef<(opts?: { keepalive?: boolean }) => boolean>(() => false);
  const onRejected = useCallback(
    (forEncounter: string, kind?: 'fall' | 'pain', signal?: AbortSignal, rev?: number) => (error: unknown) => {
      if (isSignedNoteRefusal(error)) {
        refusedRef.current = forEncounter;
        setRefusedFor(forEncounter);
        return;
      }
      if (kind && isStaleRiskSave(error)) {
        // PROD-3142: never overwrite the newer list. Its edits stay pending here, unsent; the page asks for a reload.
        const base = conflictRef.current[forEncounter] ?? { fall: false, pain: false };
        conflictRef.current = { ...conflictRef.current, [forEncounter]: { ...base, [kind]: true } };
        setConflict(conflictRef.current);
        return;
      }
      // PARITY-DEFECT(risk-save-errors): swallowed, as before — unless frame v2 asks for failures. A save replaced by a
      // newer one (aborted) is not a failure.
      if (!reportFailures || !kind || signal?.aborted) return;
      if (unmountedRef.current) return;
      dirtyRef.current = true;
      if (retryMs !== undefined && rev !== undefined) {
        // PROD-3065b: the first failure of a revision is retried once, automatically, after a short delay (the retry sends
        // the latest edits). The allowance is the revision's own, so an older save failing late cannot use up a newer
        // edit's retry (Codex).
        if (retriedRevRef.current[kind] === rev) {
          setFailed((f) => ({ ...f, [kind]: true }));
          return;
        }
        retriedRevRef.current[kind] = rev;
        if (!retryTimerRef.current) {
          setRetryWaiting(true);
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setRetryWaiting(false);
            flushRef.current();
          }, retryMs);
        }
        return;
      }
      setFailed((f) => ({ ...f, [kind]: true }));
    },
    [reportFailures, retryMs],
  );
  const onSaved = useCallback((kind: 'fall' | 'pain') => () => setFailed((f) => (f[kind] ? { ...f, [kind]: false } : f)), []);

  // In-flight saves (PROD-2947): counted, so the frame v2 sign can wait for them.
  const [inFlight, setInFlight] = useState(0);
  const track = useCallback(<T,>(p: Promise<T>): Promise<T | void> => {
    setInFlight((n) => n + 1);
    return p.finally(() => setInFlight((n) => n - 1));
  }, []);

  // The latest `send`, for `flush` (synced after commit, as `flushRef`).
  const sendRef = useRef<(kind: 'fall' | 'pain', forEncounter: string, keepalive?: boolean) => boolean>(() => false);
  // The latest guarded one-list save, for the queued resend (synced after commit, as `flushRef`).
  const flushOneRef = useRef<(kind: 'fall' | 'pain', forEncounter: string, keepalive?: boolean) => void>(() => {});
  /** One list's save. Unversioned (the API sends no version): abort the previous save and send, as before. Versioned
   *  (PROD-3142): queue behind a save in flight, send the version the list was edited from, keep the one returned. */
  const send = useCallback(
    (kind: 'fall' | 'pain', forEncounter: string, keepalive = false): boolean => {
      const expectedVersion = versionRef.current.for === forEncounter ? versionRef.current[kind] : undefined;
      const queue = queueFor(forEncounter);
      if (expectedVersion !== undefined && queue.inFlight[kind]) {
        queue.queued[kind] = true;
        // Sticky until the waiting edit is sent: a later ordinary flush must not drop a page-hide's keepalive.
        if (keepalive) queue.queuedKeepalive[kind] = true;
        return true;
      }
      const abortRef = kind === 'fall' ? fallAbortRef : painAbortRef;
      if (expectedVersion === undefined) abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;
      // PARITY-DEFECT(risk-save-errors): Flutter awaits the save and discards the result — a failed save surfaces NOTHING
      // to the user. Reproduced deliberately: the rejection is swallowed here (unless frame v2 asks for failures), and
      // never rethrown.
      const opts = { signal: abort.signal, ...(expectedVersion ? { expectedVersion } : {}), ...(keepalive ? { keepalive } : {}) };
      const save: Promise<string | undefined> =
        kind === 'fall'
          ? postFallAssessments(forEncounter, fallAssessmentsRef.current, opts)
          : postPainAssessments(forEncounter, painAssessmentsRef.current, opts);
      void track(save.catch(onRejected(forEncounter, kind, abort.signal, revRef.current[kind])));
      void save.then(onSaved(kind), () => {});
      if (expectedVersion !== undefined) {
        queue.inFlight[kind] = true;
        // Registered after `onRejected`, so a refusal or a conflict is latched before the waiting edit is considered.
        // On success the waiting edit goes from the version this save left; after a failure it goes anyway (Codex P1:
        // the flush that queued it already cleared the dirty flag). `flushOne` re-checks every guard first.
        const settle = () => {
          queue.inFlight[kind] = false;
          if (!queue.queued[kind]) return;
          queue.queued[kind] = false;
          const keep = queue.queuedKeepalive[kind];
          queue.queuedKeepalive[kind] = false;
          flushOneRef.current(kind, forEncounter, keep);
        };
        void save.then(
          (version) => {
            if (version !== undefined && versionRef.current.for === forEncounter) versionRef.current[kind] = version;
            settle();
          },
          settle,
        );
      }
      return true;
    },
    [onRejected, onSaved, track],
  );
  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  /** One list's waiting edit, with every guard `flush` applies (Codex P1): only for the visit still open, never after
   *  Discard, a signed refusal, a failed read or a conflict on that list. */
  const flushOne = useCallback(
    (kind: 'fall' | 'pain', forEncounter: string, keepalive = false) => {
      if (!encounterId || forEncounter !== encounterId) return;
      if (refusedRef.current === encounterId) return;
      if (isNoteSessionDiscarded(noteSession)) return;
      if (readFailedRef.current[kind]) return;
      if (conflictRef.current[encounterId]?.[kind]) return;
      sendRef.current(kind, encounterId, keepalive);
    },
    [encounterId, noteSession],
  );
  useEffect(() => {
    flushOneRef.current = flushOne;
  }, [flushOne]);

  const flush = useCallback((opts: { keepalive?: boolean } = {}): boolean => {
    if (!dirtyRef.current || !encounterId) return false;
    if (refusedRef.current === encounterId) return false; // refused as signed: never re-sent
    // PROD-3107: after "Discard" in the leave guard, the unmount (or page-hide) save must not send the discarded edits.
    if (isNoteSessionDiscarded(noteSession)) return false;
    // A save now (a sign, a step change, a leave, the pause) IS the waiting retry: it sends the same pending edits.
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      setRetryWaiting(false);
    }
    dirtyRef.current = false;
    let started = false;
    const conflicted = conflictRef.current[encounterId] ?? { fall: false, pain: false };

    if (!readFailedRef.current.fall && !conflicted.fall) {
      started = sendRef.current('fall', encounterId, opts.keepalive) || started;
    }
    if (!readFailedRef.current.pain && !conflicted.pain) {
      started = sendRef.current('pain', encounterId, opts.keepalive) || started;
    }
    return started;
  }, [encounterId, noteSession]);


  // Flush on unmount (the encounter page, which owns this hook since PROD-2577 — it also calls
  // `flush` on every step change) and on the tab being hidden/closed, approximating "encounter-
  // level save (navigation / exit / sign)". Same after-commit rule as above: the ref write moves
  // to an effect so render stays pure, and `onHide`/the unmount cleanup only ever run after a
  // commit anyway.
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  // PROD-2947 · frame v2 (B5): an edit saves on a pause. The timer restarts on every edit; a server hydration is not an
  // edit (it never sets the dirty flag), so loading saves nothing.
  useEffect(() => {
    if (!autosaveMs || !dirtyRef.current) return;
    const t = setTimeout(() => flushRef.current(), autosaveMs);
    return () => clearTimeout(t);
  }, [fallAssessments, painAssessments, autosaveMs]);

  useEffect(() => {
    // Mounted (again, under Strict Mode's mount → cleanup → mount): failures are reported once more.
    unmountedRef.current = false;
    // `keepalive`: a write started as the page is hidden or unloaded must survive it. Without the flag the browser may
    // cancel it, and the last Fall / Pain edit was lost on a refresh (dev repro, 10 Oct 2026).
    function onHide() {
      flushRef.current({ keepalive: true });
    }
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      flushRef.current(); // also sends (and so cancels) a waiting retry
      unmountedRef.current = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  return {
    fallAssessments,
    painAssessments,
    updateFallAssessments,
    updatePainAssessments,
    isLoaded:
      (fallQuery.data !== undefined || fallQuery.isError) && (painQuery.data !== undefined || painQuery.isError),
    readFailed,
    serverFallAssessments: fallQuery.data?.entries,
    serverPainAssessments: painQuery.data?.entries,
    flush,
    saving: inFlight > 0 || retryWaiting,
    saveFailed: failed.fall || failed.pain,
    saveRefused: !!encounterId && refusedFor === encounterId,
    saveConflict: { fall: !!encounterId && !!conflict[encounterId]?.fall, pain: !!encounterId && !!conflict[encounterId]?.pain },
  };
}
